import { Component, type ErrorInfo, type ReactNode } from "react";

// Imported directly rather than via useI18n(): this boundary wraps the
// providers, so there is no context to read. It never needs to re-render on a
// language change either - by the time it is showing, the app has crashed.
import { t } from "@/i18n/t";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Catches render-time exceptions.
 *
 * Without this, React unmounts the whole tree on an uncaught render error and
 * the user is left staring at a blank white page with no indication of what
 * happened and nothing to report. This turns that into a readable message plus
 * the stack, so a bug can actually be described.
 *
 * Must be a class component: there is no hook equivalent of
 * componentDidCatch/getDerivedStateFromError.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep it in the console as well, so it is visible in devtools and any
    // future log forwarding picks it up.
    console.error("Unhandled render error:", error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render(): ReactNode {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="grid min-h-dvh place-items-center bg-page p-6">
        <div className="w-full max-w-[680px] rounded-card bg-surface px-7 py-6 shadow-card">
          <div className="mb-3.5 grid size-10 place-items-center rounded-full bg-danger text-[20px] font-bold text-white">
            !
          </div>

          <h1 className="mb-1.5 text-[23px]">{t("error.title")}</h1>
          <p className="mb-5 text-[14px] text-muted">{t("error.body")}</p>

          <div className="mb-5 overflow-x-auto rounded-[9px] bg-page px-3.5 py-3">
            <code className="font-mono text-[12.5px] break-words text-danger">
              {error.name}: {error.message}
            </code>
            {componentStack && (
              <details className="mt-2.5">
                <summary className="cursor-pointer text-[12px] text-muted">
                  {t("error.details")}
                </summary>
                <pre className="mt-2 max-h-[240px] overflow-auto font-mono text-[11px] whitespace-pre-wrap text-muted">
                  {componentStack.trim()}
                </pre>
              </details>
            )}
          </div>

          <div className="flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-[9px] bg-navy px-4 py-2.5 text-[14.5px] font-medium text-white hover:bg-navy-soft"
            >
              {t("error.reload")}
            </button>
            <button
              type="button"
              // A full navigation, not a router push: the router lives inside
              // this boundary and its state is what just failed.
              onClick={() => window.location.assign("/")}
              className="rounded-[9px] border border-line bg-surface px-4 py-2.5 text-[14.5px] font-medium text-ink hover:bg-line-soft"
            >
              {t("error.toOverview")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
